import { StyleProvider } from "@ant-design/cssinjs";
import { ChakraProvider } from "@chakra-ui/react";
import { UIEnvironmentService, serverEnvironment } from "@condoit-apps/config/src";
import { withSentry } from "@sentry/remix";
import React, { useEffect } from "react";
import type { LinksFunction, LoaderFunctionArgs, MetaFunction } from "react-router";
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData } from "react-router";
import { ErrorBoundary } from "./components/SomethingWentWrong";
import toggleSelectorStyles from "./components/ToggleSelector/toggleSelector.css?url";
import { EnvironmentProvider } from "./contexts/environmentContext";
import globalsStyles from "./styles/globals.css?url";
import { chakraTheme } from "./theme/chakraTheme";
import * as Sentry from "@sentry/remix";
import { browserOptions } from "../sentry.config.client";

export const meta: MetaFunction = () => {
  return [{ title: "Condoit" }, { name: "description", content: "Condoit" }];
};

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },

  { rel: "stylesheet", href: toggleSelectorStyles },

  { rel: "stylesheet", href: globalsStyles }, //has tailwind styles
];
const DEFAULT_COLOR_MODE: "dark" | "light" | null = "light";

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
  const env = new UIEnvironmentService();

  return {
    env,
    IS_RENDER_ENVIRONMENT: serverEnvironment.IS_RENDER_ENVIRONMENT,
    IS_RENDER_PREVIEW_ENVIRONMENT: serverEnvironment.IS_RENDER_PREVIEW_ENVIRONMENT,
  };
};

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useLoaderData<typeof loader>();
  const { env, IS_RENDER_ENVIRONMENT, IS_RENDER_PREVIEW_ENVIRONMENT } = data || {};
  const pendoKey = env?.PKEY;

  useEffect(() => {
    if (Sentry.isInitialized()) {
      return;
    }

    const environment = IS_RENDER_PREVIEW_ENVIRONMENT
      ? "preview"
      : env?.env === "production"
        ? "production"
        : "development";

    Sentry.init({
      ...browserOptions,
      enabled: IS_RENDER_ENVIRONMENT, // Only enable Sentry on Render servers
      environment,
    });
  }, [env, IS_RENDER_ENVIRONMENT, IS_RENDER_PREVIEW_ENVIRONMENT]);

  const pendoScript = pendoKey
    ? ` 
          (function(apiKey){ 
              (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
          
          v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){ 
          
          o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]); 
          
          y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js'; 
          
          z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo'); 
          })('${pendoKey}');
      `
    : "";

  return (
    <html
      lang="en"
      {...(DEFAULT_COLOR_MODE && {
        "data-theme": DEFAULT_COLOR_MODE,
        style: { colorScheme: DEFAULT_COLOR_MODE },
      })}
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {pendoScript && <script dangerouslySetInnerHTML={{ __html: pendoScript }} />}
      </head>
      <body
        {...(DEFAULT_COLOR_MODE && {
          className: `chakra-ui-${DEFAULT_COLOR_MODE}`,
        })}
      >
        <StyleProvider hashPriority="high">
          <ChakraProvider theme={chakraTheme}>
            <EnvironmentProvider env={env}>{children}</EnvironmentProvider>
          </ChakraProvider>
        </StyleProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export { ErrorBoundary };

export function App() {
  return <Outlet />;
}

export default withSentry(App, {
  wrapWithErrorBoundary: false,
});
